import React from 'react';

// Utils
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';

import sectionCss from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.module.css';

export const SIZE_LISTING_FIELD_KEYS = ['clothingSize', 'shoeSize'];

const hasRenderableValue = value => value !== null && typeof value !== 'undefined' && value !== '';

/**
 * Renders custom listing fields.
 * - SectionDetails is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnum is used if schemaType is 'multi-enum'
 * - SectionText is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const {
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    intl,
    allowedFieldKeys,
  } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  // Fields are shown by default. Set showConfig.displayOnListingPage to false to explicitly hide a field.
  const allowedFieldKeysMaybe = Array.isArray(allowedFieldKeys) ? allowedFieldKeys : null;
  const displayableFieldConfigs = listingFieldConfigs.filter(fieldConfig => {
    const isAllowedField =
      !allowedFieldKeysMaybe || allowedFieldKeysMaybe.includes(fieldConfig.key);
    return isAllowedField && fieldConfig.showConfig?.displayOnListingPage !== false;
  });

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const isSizeOnlySection =
    Array.isArray(allowedFieldKeysMaybe) &&
    allowedFieldKeysMaybe.every(key => SIZE_LISTING_FIELD_KEYS.includes(key));

  const sectionDetailsProps = {
    ...props,
    isFieldForCategory: isFieldForSelectedCategories,
    fieldConfigs: displayableFieldConfigs,
    // Size-only buy-item section: no section heading — the field row already says "Size".
    heading: isSizeOnlySection ? null : 'ListingPage.detailsTitle',
    rootClassName: isSizeOnlySection
      ? sectionCss.sectionDetailsInBuyItem
      : props.rootClassName,
  };

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && hasRenderableValue(value)) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return hasRenderableValue(detailValue?.value)
        ? filteredConfigs.concat(detailValue)
        : filteredConfigs;
    }
    return filteredConfigs;
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="listingPage"
      pickExtendedDataFields={pickExtendedDataFields}
    />
  );
};

export default CustomListingFields;
